import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { SignalsAPI, type Signal } from '@/api/signals'
import { GraphAPI, type GraphNetwork } from '@/api/graph'
import { MissionsAPI, type MissionStep } from '@/api/missions'
import { briefSignals } from '@/lib/brief'

// The Action Brief — the lead surface. Answers the FI feedback ("make it actionable"): each card is
// what changed → the warm path to who matters → a one-click action. The constellation is the proof
// underneath; this is the pitch.

const IMP_COLOR: Record<string, string> = {
  High: 'var(--ae-color-feedback-error)',
  Medium: 'var(--ae-color-accent)',
  Low: 'var(--ae-color-text-muted)',
}

const OUTREACH_STEPS: MissionStep[] = [
  { id: 'Context selected', status: 'done' },
  { id: 'Generate draft', status: 'running' },
  { id: 'Review tone', status: 'pending' },
  { id: 'Log touch', status: 'pending' },
]

// Shortest path from the ego node to a target over the undirected edge set (the warm path).
function warmPath(egoId: string, targetId: string, net: GraphNetwork): string[] | null {
  if (egoId === targetId) return [egoId]
  const adj = new Map<string, string[]>()
  for (const e of net.edges) {
    ;(adj.get(e.source) ?? adj.set(e.source, []).get(e.source)!).push(e.target)
    ;(adj.get(e.target) ?? adj.set(e.target, []).get(e.target)!).push(e.source)
  }
  const prev = new Map<string, string>()
  const seen = new Set([egoId])
  let frontier = [egoId]
  while (frontier.length) {
    const next: string[] = []
    for (const id of frontier) {
      for (const nb of adj.get(id) ?? []) {
        if (seen.has(nb)) continue
        seen.add(nb)
        prev.set(nb, id)
        if (nb === targetId) {
          const path = [nb]
          let cur = id
          while (cur !== egoId) { path.unshift(cur); cur = prev.get(cur)! }
          path.unshift(egoId)
          return path
        }
        next.push(nb)
      }
    }
    frontier = next
  }
  return null
}

interface BriefItem {
  signal: Signal
  targetName: string | null
  pathNames: string[] | null // ego → … → target, or null if no path found
}

export function Brief() {
  const { diwaUrl } = useUI()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: signals } = useQuery({ queryKey: ['signals'], queryFn: () => new SignalsAPI(diwaUrl).list() })
  const { data: net } = useQuery({ queryKey: ['graph-network', 'all'], queryFn: () => new GraphAPI(diwaUrl).network('all') })
  const [busy, setBusy] = useState<string | null>(null)

  const items: BriefItem[] = useMemo(() => {
    if (!signals) return []
    const nodes = net?.nodes ?? []
    const ego = nodes.find((n) => n.self)
    const byName = new Map(nodes.map((n) => [n.name.toLowerCase(), n]))
    return briefSignals(signals)
      .map((signal) => {
        // resolve the first related entity that's a real, non-event node
        const target = signal.related
          .map((r) => byName.get(r.toLowerCase()))
          .find((n) => n && n.type !== 'event' && !n.self)
        let pathNames: string[] | null = null
        if (ego && target && net) {
          const ids = warmPath(ego.id, target.id, net)
          if (ids) {
            const nameById = new Map(nodes.map((n) => [n.id, n.name]))
            pathNames = ids.map((id) => (id === ego.id ? 'You' : nameById.get(id) ?? '?'))
          }
        }
        return { signal, targetName: target?.name ?? null, pathNames }
      })
  }, [signals, net])

  async function createMission(item: BriefItem, protocol: string, titlePrefix: string, steps?: MissionStep[]) {
    const target = item.targetName ?? 'target'
    const key = item.signal.id + protocol
    setBusy(key)
    try {
      const created = await new MissionsAPI(diwaUrl).create({
        title: `${titlePrefix} ${target}`,
        goal: item.signal.suggested_action || `Act on: ${item.signal.title}`,
        status: 'Active',
        priority: (['High', 'Medium', 'Low'].includes(item.signal.importance) ? item.signal.importance : 'Medium') as 'High' | 'Medium' | 'Low',
        domain: item.signal.domain === 'markets' ? 'Markets' : 'Capital Network',
        target,
        protocol,
        steps: steps ?? [
          { id: 'Target defined', status: 'done' },
          { id: 'Enrich target', status: 'running' },
          { id: 'Find warm path', status: 'pending' },
          { id: 'Draft intro', status: 'pending' },
          { id: 'Track response', status: 'pending' },
        ],
      })
      await qc.invalidateQueries({ queryKey: ['missions'] })
      navigate(`/missions/${created.id}`)
    } finally {
      setBusy(null)
    }
  }

  if (items.length === 0) return null // nothing still needs you: no empty brief header

  return (
    <div style={{ marginBottom: 24 }}>
      <div className="dw-section-header" style={{ marginTop: 0 }}>
        <div>
          <div className="dw-page-title" style={{ fontSize: 'var(--ae-font-size-heading-sm, 18px)' }}>Your brief</div>
          <div className="dw-page-subtitle" style={{ marginTop: 2 }}>What changed, the warm path, and the next move.</div>
        </div>
        <span onClick={() => navigate('/signals')} style={{ fontSize: 'calc(11px * var(--ae-text-scale, 1))', color: 'var(--ae-color-accent)', cursor: 'pointer' }}>All signals →</span>
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        {items.map((item) => {
          const accent = IMP_COLOR[item.signal.importance] ?? 'var(--ae-color-text-muted)'
          const canAct = !!item.targetName
          return (
            <Card key={item.signal.id} className="dw-accent" style={{ ['--dw-accent' as string]: accent }}>
              {/* insight */}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--ae-font-size-sm)' }}>{item.signal.title}</div>
                <span style={{ fontSize: 'calc(10px * var(--ae-text-scale, 1))', textTransform: 'uppercase', letterSpacing: '0.12em', color: accent, whiteSpace: 'nowrap' }}>
                  {item.signal.importance}
                </span>
              </div>

              {/* warm path */}
              <div style={{ margin: '9px 0 2px', fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-text-secondary)' }}>
                {item.pathNames ? (
                  <span>
                    {item.pathNames.map((n, i) => (
                      <span key={i}>
                        {i > 0 && <span style={{ color: 'var(--ae-color-text-muted)' }}> → </span>}
                        <span style={{ color: i === item.pathNames!.length - 1 ? accent : 'var(--ae-color-text-primary)' }}>{n}</span>
                      </span>
                    ))}
                  </span>
                ) : item.targetName ? (
                  <span style={{ color: 'var(--ae-color-text-muted)' }}>No warm path yet — {item.targetName} is a cold target.</span>
                ) : (
                  <span style={{ color: 'var(--ae-color-text-muted)' }}>Monitor — no single target to route to.</span>
                )}
              </div>

              {/* action */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                {item.signal.suggested_action && (
                  <span style={{ fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-accent)', marginRight: 4 }}>→ {item.signal.suggested_action}</span>
                )}
                {canAct && (
                  <>
                    <button className="dw-btn" disabled={!!busy} onClick={() => createMission(item, 'Draft outreach', 'Draft outreach to', OUTREACH_STEPS)}>
                      ✉ Draft intro
                    </button>
                    <button className="dw-action-btn" disabled={!!busy} onClick={() => createMission(item, 'Capital network path', 'Open path to')}>
                      ◱ Open as mission
                    </button>
                  </>
                )}
                <button className="dw-action-btn" onClick={() => navigate('/graph')}>Show path</button>
              </div>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
